import { useForumType } from '@/components/hooks/useForumType';
import { registerComponent } from '../../lib/vulcan-lib/components';
import { getSiteUrl } from '../../lib/vulcan-lib/utils';
import classNames from 'classnames';
import React, { FC, ReactNode, useCallback, useState } from 'react';
import { Card } from "@/components/widgets/Paper";
import { getNotificationTypeByName } from '../../lib/notificationTypes';
import withErrorBoundary from '../common/withErrorBoundary';
import { parseRouteWithErrors } from '@/lib/routeChecks/parseRouteWithErrors';
import { useTracking } from '../../lib/analyticsEvents';
import { useNavigate } from '../../lib/routeUtil';
import { getUrlClass } from '@/server/utils/getUrlClass';
import LWTooltip from "../common/LWTooltip";
import PostsTooltip from "../posts/PostsPreviewTooltip/PostsTooltip";
import ConversationPreview from "../messaging/ConversationPreview";
import PostNominatedNotification from "../review/PostNominatedNotification";
import TagRelNotificationItem from "./TagRelNotificationItem";
import { onsiteHoverViewComponents } from '@/lib/notificationTypeComponents';
import { getNotificationIconByNotificationName } from './notificationIcons';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const styles = defineStyles('NotificationsItem', (theme: ThemeType) => ({
  root: {
    "&:hover": {
      backgroundColor: `${theme.palette.panelBackground.darken02} !important`,
    },
    display: "flex",
    alignItems: "center",
    padding: 0,
    borderBottom: theme.palette.border.faint,

    // Disable MUI's hover-highlight-color animation that conflicts with having
    // a non-default background color and looks glitchy.
    transition: "none",
  },
  read: {
    backgroundColor: `${theme.palette.panelBackground.darken04} !important`,
    
    "&:hover": {
      backgroundColor: `${theme.palette.panelBackground.darken08} !important`,
    },
  },
  unread: {
    backgroundColor: "inherit !important",
  },
  preview: {
    [theme.breakpoints.down('xs')]: {
      display:"none"
    }
  },
  notificationLabel: {
    ...theme.typography.body2,
    fontSize: "14px",
    lineHeight: "18px",
    paddingRight: 16,
    color: theme.palette.text.notificationLabel,
    
    // Two-line ellipsis hack. Webkit-specific (doesn't work in Firefox),
    // inherited from old-Material-UI (where it also doesn't work in Firefox,
    // the symptom being that the ellipses are missing but the layout is
    // otherwise fine).
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
  },
}));

const tooltipProps = {
  placement: "left-start",
  pageElementContext: "linkPreview",
  pageElementSubContext: "notificationItem",
  clickable: true,
} as const;

const TooltipWrapper: FC<{
  title: ReactNode,
  children: ReactNode,
}> = ({title, children}) => {
  const classes = useStyles(styles);

  return (
    <LWTooltip
      {...tooltipProps}
      tooltip={false}
      title={
        <span className={classes.preview}>
          <Card>
            {title}
          </Card>
        </span>
      }
    >
      {children}
    </LWTooltip>
  );
}

const NotificationsItem = ({notification, lastNotificationsCheck}: {
  notification: NotificationsList,
  lastNotificationsCheck: any,
}) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const [clicked,setClicked] = useState(false);
  const { captureEvent } = useTracking();
  const navigate = useNavigate();
  const notificationType = getNotificationTypeByName(notification.type ?? '');
  const documentId = notification.documentId ?? '';

  const notificationLink = (notificationType.getLink
    ? notificationType.getLink({
      documentType: notification.documentType,
      documentId: notification.documentId,
      extraData: notification.extraData,
    })
    : notification.link ?? ''
  );

  const PreviewTooltip: FC<{children: ReactNode}> = useCallback(({children}) => {
    const OnsiteHoverView = onsiteHoverViewComponents[notificationType.name]?.() ?? null;
    if (OnsiteHoverView) {
      return (
        <TooltipWrapper
          title={<OnsiteHoverView notification={notification}/>}
        >
          {children}
        </TooltipWrapper>
      );
    }

    if (notification.type === "postNominated") {
      return (
        <TooltipWrapper
          title={<PostNominatedNotification postId={documentId}/>}
        >
          {children}
        </TooltipWrapper>
      );
    }

    if (notification.type === "newDialogueMessages") {
      const dialogueMessageInfo = notification.extraData?.dialogueMessageInfo
      const postId = notification.documentId ?? undefined
      return (
        <PostsTooltip postId={postId} dialogueMessageInfo={dialogueMessageInfo} {...tooltipProps}>
          {children}
        </PostsTooltip>
      )
    }

    const parsedPath = parseRouteWithErrors(notificationLink);
    switch (notification.documentType) {
      case "tagRel":
        return (
          <PostsTooltip tagRelId={documentId} {...tooltipProps}>
            {children}
          </PostsTooltip>
        );
      case "post":
        return (
          <PostsTooltip postId={documentId} {...tooltipProps}>
            {children}
          </PostsTooltip>
        );
      case "comment":
        const postId = parsedPath?.params?._id;
        return postId
          ? (
            <PostsTooltip
              postId={postId}
              commentId={documentId}
              {...tooltipProps}
            >
              {children}
            </PostsTooltip>
          )
          : <>
            {children}
          </>
      case "message":
        return (
          <TooltipWrapper
            title={<ConversationPreview conversationId={parsedPath?.query?.conversation} messageId={documentId} count={1} />}
          >
            {children}
          </TooltipWrapper>
        );
      default:
        break;
    }

    return (
      <>{children}</>
    );
  }, [notification, notificationLink, notificationType, documentId]);

  const renderMessage = () => {
    switch (notification.documentType) {
      // TODO: add case for tagRel
      case 'tagRel': 
        return <TagRelNotificationItem tagRelId={documentId}/>
      default:
        return notification.message
    }
  }
  
  return (
    <PreviewTooltip>
      <a
        href={notificationLink}
        className={classNames(
          classes.root,
          {
            [classes.read]:     (!notification.createdAt || notification.createdAt < lastNotificationsCheck) || clicked,
            [classes.unread]: !((!notification.createdAt || notification.createdAt < lastNotificationsCheck) || clicked)
          }
        )}
        onClick={(ev) => {
          if (ev.button>0 || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.metaKey)
            return;
            
          captureEvent("notificationItemClick", {
            notification: {
              _id: notification._id,
              type: notification.type,
              documentId: notification.documentId,
              documentType: notification.documentType,
              link: notification.link,
            }
          });
          
          // Do manual navigation since we also want to do a bunch of other stuff
          ev.preventDefault()
          navigate(notificationLink)

          setClicked(true);
          
          // we also check whether it's a relative link, and if so, scroll to the item
          const UrlClass = getUrlClass()
          const url = new UrlClass(notificationLink, getSiteUrl(forumType))
          const hash = url.hash
          if (hash) {
            const element = document.getElementById(hash.substr(1))
            if (element) element.scrollIntoView({behavior: "smooth"});
          }
        }}
      >
        {notification.type ? getNotificationIconByNotificationName(notification.type) : null}
        <div className={classes.notificationLabel}>
          {renderMessage()}
        </div>
      </a>
    </PreviewTooltip>
  );
}

export default registerComponent('NotificationsItem', NotificationsItem, {
  hocs: [withErrorBoundary]
});


